/**
 * OpsInboxPage — Requests Queue.
 * Visible to Executive Director + Operations Manager.
 * Route: /ops/inbox
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useOpsAuth, opsApi, ROLE_LABELS_AR, canManageInbox, formatAuthError } from './OpsAuthContext';
import OpsLayout from './OpsLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SOURCE_LABELS = {
  service_request: 'طلب خدمة',
  improvement_plan: 'خطة تحسين',
  quotation: 'عرض سعر',
  talent_profile: 'ملف كفاءة',
  talent_request: 'طلب كفاءات',
  store_purchase: 'شراء رقمي',
};

const STATUS_LABELS = {
  new: { label: 'جديد', color: 'bg-[#DEE7CB] text-[#3F5021]' },
  in_review: { label: 'قيد المراجعة', color: 'bg-[#FDF3D6] text-[#8A6A18]' },
  routed: { label: 'تم التحويل', color: 'bg-[#E3EDDE] text-[#4A5D23]' },
  dismissed: { label: 'مُلغى', color: 'bg-[#F0E3E1] text-[#7A4B44]' },
};

const OpsInboxPage = () => {
  const { user } = useOpsAuth();
  const [entries, setEntries] = useState([]);
  const [counts, setCounts] = useState({ new: 0, in_review: 0, routed: 0, dismissed: 0, total: 0 });
  const [team, setTeam] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [filter, setFilter] = useState('new');
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [banner, setBanner] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter === 'all' ? '' : `?status=${filter}`;
      const { data } = await opsApi.get(`/inbox${q}`);
      setEntries(data.entries);
      setCounts(data.counts);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    opsApi.get('/users').then((r) => setTeam(r.data)).catch(() => {});
    opsApi.get('/tracks').then((r) => setTracks(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const selected = useMemo(() => entries.find((e) => e.id === selectedId), [entries, selectedId]);

  const exportExcel = async () => {
    const params = new URLSearchParams();
    if (filter && filter !== 'all') params.set('status', filter);
    const url = `${API_URL}/api/ops/inbox/export.xlsx${params.toString() ? `?${params.toString()}` : ''}`;
    try {
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const dispo = res.headers.get('Content-Disposition') || '';
      const match = /filename="?([^";]+)"?/i.exec(dispo);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = match ? match[1] : 'inbox_requests.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 3000);
    } catch (e) {
      setBanner('تعذّر تصدير الملف — حاولي مرة أخرى');
      setTimeout(() => setBanner(''), 3500);
    }
  };

  return (
    <OpsLayout title="صندوق الطلبات" subtitle="Inbox · Requests Queue">
      {banner && (
        <div className="mb-4 bg-[#E3EDDE] border border-[#B8CBA2] text-[#2C3814] rounded-xl px-4 py-2.5 text-[13px] font-['Noto_Kufi_Arabic']">
          {banner}
        </div>
      )}

      {/* Counters */}
      <div className="flex items-center justify-between mb-3 gap-3">
        <p className="text-[11px] text-[#7A866E] font-['Noto_Kufi_Arabic']">
          كل طلب يظهر بحقوله كاملة، وكل حقل عمود مستقل عند التصدير.
        </p>
        <button
          onClick={exportExcel}
          data-testid="inbox-export-excel"
          className="text-[12px] font-['Noto_Kufi_Arabic'] font-semibold bg-white border border-[#4A5D23] text-[#4A5D23] hover:bg-[#4A5D23] hover:text-white transition px-4 py-2 rounded-lg"
        >
          تصدير إلى Excel
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          { key: 'all', label: 'الإجمالي', val: counts.total },
          { key: 'new', label: 'جديد', val: counts.new },
          { key: 'in_review', label: 'قيد المراجعة', val: counts.in_review },
          { key: 'routed', label: 'مُحوَّل', val: counts.routed },
          { key: 'dismissed', label: 'مُلغى', val: counts.dismissed },
        ].map((c) => (
          <button
            key={c.key}
            data-testid={`inbox-filter-${c.key}`}
            onClick={() => { setFilter(c.key); setSelectedId(null); }}
            className={`text-start px-4 py-3 rounded-xl border transition ${
              filter === c.key
                ? 'bg-[#1F3D2B] text-white border-[#1F3D2B]'
                : 'bg-white text-[#1F3D2B] border-[#E6E0D6] hover:border-[#4A5D23]'
            }`}
          >
            <div className="text-[11px] font-['Noto_Kufi_Arabic'] opacity-80">{c.label}</div>
            <div className="font-['Playfair_Display'] text-2xl font-semibold mt-1 leading-none">{c.val}</div>
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1fr_1.15fr] gap-6">
        {/* List */}
        <section className="bg-white rounded-2xl ring-1 ring-[#E6E0D6] overflow-hidden">
          <header className="px-5 py-3 border-b border-[#EFEAE1] flex items-center justify-between">
            <h3 className="font-['Noto_Kufi_Arabic'] text-[14px] font-bold text-[#1F3D2B]">
              {entries.length} طلب
            </h3>
            <button
              onClick={refresh}
              data-testid="inbox-refresh"
              className="text-[11px] text-[#4A5D23] font-['Noto_Kufi_Arabic'] hover:underline"
            >
              تحديث
            </button>
          </header>
          <ul className="divide-y divide-[#EFEAE1] max-h-[70vh] overflow-y-auto">
            {loading && (
              <li className="p-6 text-center text-[13px] text-[#7A866E] font-['Noto_Kufi_Arabic']">
                جارٍ التحميل…
              </li>
            )}
            {!loading && entries.length === 0 && (
              <li
                data-testid="inbox-empty"
                className="p-8 text-center text-[13px] text-[#7A866E] font-['Noto_Kufi_Arabic']"
              >
                لا توجد طلبات في هذه الحالة
              </li>
            )}
            {entries.map((e) => (
              <li
                key={e.id}
                data-testid={`inbox-item-${e.id}`}
                onClick={() => setSelectedId(e.id)}
                className={`p-4 cursor-pointer transition ${
                  selectedId === e.id ? 'bg-[#F3F1E9]' : 'hover:bg-[#FAFAF7]'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-['Noto_Kufi_Arabic'] text-[14px] font-bold text-[#1F3D2B] truncate">
                      {e.client_name || '—'}
                    </p>
                    <p className="font-['Noto_Kufi_Arabic'] text-[11px] text-[#7A866E] mt-0.5 truncate">
                      {e.client_org || 'بدون جهة'} · {SOURCE_LABELS[e.source_type] || e.source_type}
                    </p>
                    <p className="font-['Noto_Kufi_Arabic'] text-[11px] text-[#4A5D23] mt-1">
                      المسار المقترح: {(tracks.find((t) => t.id === e.suggested_track_id)?.name) || '—'}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 text-[10px] font-['Noto_Kufi_Arabic'] font-semibold px-2 py-1 rounded-full ${STATUS_LABELS[e.status]?.color}`}
                  >
                    {STATUS_LABELS[e.status]?.label}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* Detail + Route form */}
        <section className="bg-white rounded-2xl ring-1 ring-[#E6E0D6] p-6 min-h-[300px]">
          {!selected && (
            <div
              data-testid="inbox-empty-detail"
              className="h-full flex items-center justify-center text-[13px] text-[#7A866E] font-['Noto_Kufi_Arabic']"
            >
              اختاري طلبًا من القائمة لعرض تفاصيله
            </div>
          )}
          {selected && (
            <InboxEntryDetail
              entry={selected}
              tracks={tracks}
              team={team}
              onChanged={(msg) => {
                setBanner(msg);
                setTimeout(() => setBanner(''), 3500);
                setSelectedId(null);
                refresh();
              }}
              canRoute={canManageInbox(user)}
            />
          )}
        </section>
      </div>
    </OpsLayout>
  );
};

// -------- Sub-component --------
const InboxEntryDetail = ({ entry, tracks, team, onChanged, canRoute }) => {
  const [trackId, setTrackId] = useState(entry.suggested_track_id || '');
  const [leadEmail, setLeadEmail] = useState(entry.suggested_lead_email || '');
  const [additionalLeads, setAdditionalLeads] = useState([]);
  const [note, setNote] = useState('');
  const [dismissReason, setDismissReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [sourceItems, setSourceItems] = useState([]);
  const [sourceLoading, setSourceLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setSourceLoading(true);
    opsApi
      .get(`/inbox/${entry.id}/source`)
      .then((r) => { if (alive) setSourceItems(r.data.items || []); })
      .catch(() => { if (alive) setSourceItems([]); })
      .finally(() => { if (alive) setSourceLoading(false); });
    return () => { alive = false; };
  }, [entry.id]);

  const track = tracks.find((t) => t.id === trackId);
  const leadCandidates = useMemo(() => {
    if (!track) return team.filter((u) => u.role === 'track_leader' || u.role === 'executive_director');
    return team.filter(
      (u) => u.email === track.leader_email || track.member_emails.includes(u.email)
    );
  }, [track, team]);

  const submitRoute = async () => {
    setErr('');
    if (!trackId || !leadEmail) {
      setErr('اختاري المسار والقائدة قبل التحويل');
      return;
    }
    setBusy(true);
    try {
      await opsApi.post(`/inbox/${entry.id}/route`, {
        track_id: trackId,
        lead_email: leadEmail,
        additional_leads: additionalLeads.filter((e) => e && e !== leadEmail),
        note,
      });
      onChanged(`تم تحويل الطلب إلى القائدة بنجاح ✅`);
    } catch (e) {
      setErr(formatAuthError(e));
    } finally {
      setBusy(false);
    }
  };

  const submitDismiss = async () => {
    setBusy(true);
    try {
      await opsApi.post(`/inbox/${entry.id}/dismiss`, { reason: dismissReason });
      onChanged('تم إلغاء الطلب');
    } catch (e) {
      setErr(formatAuthError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="inbox-detail">
      <div className="border-b border-[#EFEAE1] pb-4 mb-4">
        <p className="text-[11px] tracking-widest text-[#4A5D23] font-['Noto_Kufi_Arabic'] uppercase font-semibold">
          {SOURCE_LABELS[entry.source_type] || entry.source_type}
        </p>
        <h2 className="font-['Noto_Kufi_Arabic'] text-[22px] font-bold text-[#1F3D2B] mt-1">
          {entry.client_name || '—'}
        </h2>
        <p className="font-['Noto_Kufi_Arabic'] text-[13px] text-[#5C6D49] mt-1">
          {entry.client_org || 'بدون جهة'}
        </p>
      </div>

      <div className="mb-5" data-testid="inbox-source-fields">
        <h3 className="font-['Noto_Kufi_Arabic'] text-[13px] font-bold text-[#1F3D2B] mb-3 pb-1.5 border-b border-[#EFEAE1]">
          تفاصيل النموذج
        </h3>
        {sourceLoading && (
          <p className="text-[12px] text-[#7A866E] font-['Noto_Kufi_Arabic']">جارٍ تحميل الحقول…</p>
        )}
        {!sourceLoading && sourceItems.length === 0 && (
          <p className="text-[12px] text-[#7A866E] font-['Noto_Kufi_Arabic']">لا توجد حقول لعرضها.</p>
        )}
        {!sourceLoading && sourceItems.length > 0 && (
          <dl className="grid sm:grid-cols-2 gap-x-4 gap-y-3 text-[12.5px] font-['Noto_Kufi_Arabic']">
            {sourceItems.map((it) => {
              const isLong = it.value && it.value.length > 60;
              const isLtr = ['email', 'phone', 'mobile', 'linkedin_profile_url', 'cv_file_url', 'preferred_date', 'preferred_time', 'created_at'].includes(it.key);
              return (
                <div
                  key={it.key}
                  className={isLong ? 'sm:col-span-2' : ''}
                  data-testid={`inbox-field-${it.key}`}
                >
                  <dt className="text-[#7A866E] text-[11px]">{it.label_ar}</dt>
                  <dd
                    className={`text-[#1F3D2B] mt-0.5 ${isLong ? 'bg-[#FAFAF7] rounded-lg p-3 leading-8 whitespace-pre-line' : ''}`}
                    dir={isLtr ? 'ltr' : undefined}
                  >
                    {it.value || '—'}
                  </dd>
                </div>
              );
            })}
          </dl>
        )}
      </div>

      {entry.status === 'routed' && (
        <div className="bg-[#E3EDDE] border border-[#B8CBA2] rounded-xl px-4 py-3 text-[13px] font-['Noto_Kufi_Arabic'] text-[#2C3814]">
          هذا الطلب تم تحويله بتاريخ {entry.routed_at ? new Date(entry.routed_at).toLocaleString('ar-SA') : '—'} إلى{' '}
          <strong>{entry.routed_to_email}</strong>
        </div>
      )}

      {entry.status !== 'routed' && entry.status !== 'dismissed' && canRoute && (
        <div className="bg-[#FAFAF7] rounded-xl p-4 space-y-3">
          <h4 className="font-['Noto_Kufi_Arabic'] text-[14px] font-bold text-[#1F3D2B]">تحويل الطلب</h4>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-['Noto_Kufi_Arabic'] text-[#5C6D49] mb-1">المسار</label>
              <select
                data-testid="route-track"
                value={trackId}
                onChange={(e) => {
                  setTrackId(e.target.value);
                  // Reset assignee whenever the track changes so the manager
                  // explicitly re-picks per project (per policy).
                  setLeadEmail('');
                }}
                className="w-full text-[13px] font-['Noto_Kufi_Arabic'] border border-[#D8D2C8] rounded-lg px-3 py-2 bg-white"
              >
                <option value="">اختاري</option>
                {tracks.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-['Noto_Kufi_Arabic'] text-[#5C6D49] mb-1">
                المُكلَّف بالمشروع
              </label>
              <select
                data-testid="route-lead"
                value={leadEmail}
                onChange={(e) => setLeadEmail(e.target.value)}
                disabled={!trackId}
                className="w-full text-[13px] font-['Noto_Kufi_Arabic'] border border-[#D8D2C8] rounded-lg px-3 py-2 bg-white disabled:bg-[#F5F2EC] disabled:cursor-not-allowed"
              >
                <option value="">
                  {!trackId
                    ? 'اختاري المسار أولاً'
                    : leadCandidates.length === 0
                    ? 'لا يوجد أعضاء في هذا المسار'
                    : 'اختاري المُكلَّف'}
                </option>
                {leadCandidates.map((u) => {
                  const hasEmail = Boolean(u.email && u.email.trim());
                  return (
                    <option
                      key={u.email || u.id}
                      value={hasEmail ? u.email : ''}
                      disabled={!hasEmail}
                    >
                      {u.name_ar} — {ROLE_LABELS_AR[u.role]}
                      {!hasEmail ? ' (بلا بريد)' : ''}
                    </option>
                  );
                })}
              </select>
              {trackId && leadCandidates.length === 0 && (
                <p
                  data-testid="route-empty-track-warning"
                  className="text-[11px] text-[#B84A3A] font-['Noto_Kufi_Arabic'] mt-1 leading-6"
                >
                  هذا المسار بلا أعضاء. عدّلي قائمة المسار من الإعدادات قبل التحويل.
                </p>
              )}
              {leadEmail && (
                <p className="text-[11px] text-[#5C6D49] font-['Noto_Kufi_Arabic'] mt-1" dir="ltr">
                  ✉︎ سيصل التكليف إلى: <strong dir="ltr">{leadEmail}</strong>
                </p>
              )}
            </div>
          </div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="ملاحظة اختيارية للقائد…"
            className="w-full text-[13px] font-['Noto_Kufi_Arabic'] border border-[#D8D2C8] rounded-lg px-3 py-2 min-h-[70px] bg-white"
          />

          {trackId && leadCandidates.length > 0 && (
            <div data-testid="route-extra-leads" className="bg-white border border-[#E6E0D6] rounded-lg p-3">
              <p className="text-[11px] font-['Noto_Kufi_Arabic'] text-[#5C6D49] mb-2 leading-6">
                مستشارون إضافيون على المشروع (اختياري) — يظهر المشروع في لوحاتهم ويصلهم بريد التكليف.
              </p>
              <ul className="grid sm:grid-cols-2 gap-x-3 gap-y-1.5 max-h-40 overflow-y-auto pr-1">
                {leadCandidates
                  .filter((u) => u.email && u.email !== leadEmail)
                  .map((u) => {
                    const checked = additionalLeads.includes(u.email);
                    return (
                      <li key={u.email}>
                        <label className="flex items-center gap-2 text-[12px] font-['Noto_Kufi_Arabic'] text-[#1F3D2B] cursor-pointer">
                          <input
                            type="checkbox"
                            data-testid={`route-extra-${u.email}`}
                            checked={checked}
                            onChange={(ev) => {
                              setAdditionalLeads((prev) =>
                                ev.target.checked
                                  ? [...prev, u.email]
                                  : prev.filter((x) => x !== u.email)
                              );
                            }}
                            className="accent-[#4A5D23]"
                          />
                          <span>{u.name_ar}</span>
                          <span className="text-[10px] text-[#7A866E]">— {ROLE_LABELS_AR[u.role]}</span>
                        </label>
                      </li>
                    );
                  })}
              </ul>
            </div>
          )}

          {err && (
            <p className="text-[12px] text-[#B84A3A] font-['Noto_Kufi_Arabic']">{err}</p>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              data-testid="route-submit"
              disabled={busy}
              onClick={submitRoute}
              className="bg-[#1F3D2B] text-white text-[13px] font-['Noto_Kufi_Arabic'] font-semibold px-5 py-2.5 rounded-lg hover:bg-[#163020] disabled:opacity-50"
            >
              {busy ? 'جارٍ التحويل…' : 'تحويل + إنشاء مشروع'}
            </button>
          </div>
        </div>
      )}

      {entry.status !== 'routed' && entry.status !== 'dismissed' && canRoute && (
        <details className="mt-4 group">
          <summary className="cursor-pointer text-[12px] text-[#7A4B44] font-['Noto_Kufi_Arabic']">
            إلغاء الطلب (طلب مكرر / غير مرغوب)
          </summary>
          <div className="mt-2 flex gap-2">
            <input
              type="text"
              value={dismissReason}
              onChange={(e) => setDismissReason(e.target.value)}
              placeholder="سبب الإلغاء (اختياري)"
              className="flex-1 text-[12px] font-['Noto_Kufi_Arabic'] border border-[#D8D2C8] rounded-lg px-3 py-2 bg-white"
            />
            <button
              data-testid="dismiss-submit"
              onClick={submitDismiss}
              disabled={busy}
              className="text-[12px] border border-[#7A4B44] text-[#7A4B44] font-['Noto_Kufi_Arabic'] font-semibold px-4 py-2 rounded-lg hover:bg-[#7A4B44] hover:text-white disabled:opacity-50"
            >
              إلغاء
            </button>
          </div>
        </details>
      )}
    </div>
  );
};

export default OpsInboxPage;
